import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import SendIcon from "@mui/icons-material/Send"
import LinkIcon from "@mui/icons-material/AddLinkOutlined"
import ForumIcon from "@mui/icons-material/ForumOutlined"
import AttachIcon from "@mui/icons-material/AttachFileOutlined"
import PeopleIcon from "@mui/icons-material/GroupsOutlined"

import { chatApi } from "../../api"
import { useAuth } from "../../context/AuthContext"
import { useSocket } from "../../context/SocketContext"
import { usePermissions } from "../../permissions"
import EmptyState from "../../components/EmptyState"
import { LoadingScreen } from "../../components/LoadingState"
import { avatarColor, formatDate, formatDateTime, initialsOf, relativeTime } from "../../utils/format"
import AttachWorkDialog from "./AttachWorkDialog"
import { uploadApi } from "../../api/uploads"
import { DropZone, FileChip, ImageAttachment, PendingFileChip } from "../../components/files"
import ImageEditorDialog from "../../components/files/ImageEditorDialog"
import ConversationSettings from "./ConversationSettings"
import { MOTION, transition } from "../../theme"
import { rise } from "../../motion"

/** How long after the last keystroke we tell the server typing has stopped. */
const TYPING_IDLE_MS = 3000
/** How long a received indicator survives without a refresh, in case "stopped" is lost. */
const TYPING_EXPIRY_MS = 6000

/**
 * Messages per page.
 *
 * Fifty is about two screens, so opening a thread does not immediately ask for
 * more, and scrolling up once gets a page that is worth the round trip.
 */
const PAGE_SIZE = 50

export default function ChatThread({ roomId, onRead, onRoomChanged }) {
  const { user } = useAuth()
  const { socket } = useSocket()
  const { can } = usePermissions()

  const [room, setRoom] = useState(null)
  const [participants, setParticipants] = useState([])
  const [messages, setMessages] = useState([])
  const [draft, setDraft] = useState("")
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [typingUsers, setTypingUsers] = useState([])
  const [attachOpen, setAttachOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [pendingFiles, setPendingFiles] = useState([])
  const [editing, setEditing] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(null)

  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  const scrollRef = useRef(null)
  const fileInputRef = useRef(null)
  const typingTimerRef = useRef(null)
  const typingSentRef = useRef(false)
  /* Whether the reader is at the bottom. A new message scrolls down only when
     they are — snapping someone back while they are reading last week is the
     rudest thing a chat window can do. */
  const pinnedRef = useRef(true)
  /* Set while older messages are being prepended, so the scroll effect knows to
     hold the reader's place instead of jumping to the end. */
  const restoreRef = useRef(null)

  const load = useCallback(async () => {
    if (!roomId) return
    setLoading(true)
    setError(null)
    try {
      const [roomData, messageData] = await Promise.all([
        chatApi.room(roomId),
        chatApi.messages(roomId, { limit: PAGE_SIZE }),
      ])
      setRoom(roomData.room)
      setParticipants(roomData.participants || [])
      const page = messageData.messages || []
      setMessages(page)
      // A full page means there is probably more behind it. One short page is
      // the end of the thread, and asking again would be a wasted round trip on
      // every conversation short enough to fit.
      setHasMore(page.length >= PAGE_SIZE)
      pinnedRef.current = true
    } catch (err) {
      setError(err.userMessage)
      setRoom(null)
    } finally {
      setLoading(false)
    }
  }, [roomId])

  useEffect(() => {
    setMessages([])
    setTypingUsers([])
    // Staged files belong to the thread they were staged in. Carrying them to
    // the next one sends somebody's statement into the wrong conversation.
    setPendingFiles([])
    setUploadProgress(null)
    load()
  }, [load])

  /**
   * Older messages, a page at a time.
   *
   * Keyset pagination on the oldest id we hold — not an offset. A thread gains
   * messages while somebody is reading it, and an offset page would skip or
   * repeat whatever arrived in between.
   *
   * The scroll position is the subtle part. Prepending content pushes
   * everything down by exactly the height of what was added, so the reader would
   * find themselves somewhere else entirely. The height before the prepend is
   * recorded here and the difference re-applied after the paint, which holds the
   * message they were looking at under their eyes.
   */
  const loadOlder = useCallback(async () => {
    const node = scrollRef.current
    if (!node || loadingMore || !hasMore || messages.length === 0) return

    setLoadingMore(true)
    restoreRef.current = { scrollHeight: node.scrollHeight, scrollTop: node.scrollTop }
    try {
      const data = await chatApi.messages(roomId, { limit: PAGE_SIZE, before: messages[0].id })
      const older = data.messages || []
      setHasMore(older.length >= PAGE_SIZE)
      if (older.length > 0) {
        setMessages((current) => {
          const known = new Set(current.map((m) => m.id))
          return [...older.filter((m) => !known.has(m.id)), ...current]
        })
      } else {
        restoreRef.current = null
      }
    } catch {
      // A failed page is not an empty thread. Leaving what is there and letting
      // them try again beats blanking the history on a dropped request.
      restoreRef.current = null
    } finally {
      setLoadingMore(false)
    }
  }, [roomId, messages, hasMore, loadingMore])

  /** Near the top and there is more: fetch it. */
  const onScroll = useCallback(
    (event) => {
      const node = event.currentTarget
      pinnedRef.current = node.scrollHeight - node.clientHeight - node.scrollTop < 80
      if (node.scrollTop < 120) loadOlder()
    },
    [loadOlder],
  )

  /* Mark read when the thread is opened and when a message arrives while it is
     open. The server clamps the cursor with GREATEST, so an out-of-order call
     from a slow tab cannot move it backwards. */
  const markRead = useCallback(
    (upToId) => {
      if (!roomId) return
      chatApi
        .markRead(roomId, upToId)
        .then(() => onRead?.(roomId))
        .catch(() => {})
    },
    [roomId, onRead],
  )

  useEffect(() => {
    if (!roomId || messages.length === 0) return
    markRead(messages[messages.length - 1].id)
  }, [roomId, messages, markRead])

  /* New messages, from the thread room. */
  useEffect(() => {
    if (!socket || !roomId) return undefined

    const onMessage = (message) => {
      if (String(message.chat_room_id) !== String(roomId)) return
      setMessages((current) => {
        // The sender already added it optimistically, and the socket echoes it
        // back — de-duplicating on id keeps it from appearing twice.
        if (current.some((m) => m.id === message.id)) return current
        return [...current, message]
      })
      // Whoever just spoke is no longer typing.
      setTypingUsers((current) => current.filter((t) => String(t.sender_user_id) !== String(message.created_by)))
    }

    socket.on("message", onMessage)
    return () => socket.off("message", onMessage)
  }, [socket, roomId])

  /* Typing indicators. Each carries its own expiry, so a lost "stopped typing"
     clears on its own rather than leaving somebody typing forever. */
  useEffect(() => {
    if (!socket || !roomId) return undefined

    const onTyping = (event) => {
      if (String(event.chat_room_id) !== String(roomId)) return
      if (String(event.sender_user_id) === String(user?.id)) return

      setTypingUsers((current) => {
        const others = current.filter((t) => String(t.sender_user_id) !== String(event.sender_user_id))
        if (!event.is_typing) return others
        return [...others, { ...event, expiresAt: Date.now() + TYPING_EXPIRY_MS }]
      })
    }

    socket.on("chat_typing", onTyping)
    return () => socket.off("chat_typing", onTyping)
  }, [socket, roomId, user?.id])

  useEffect(() => {
    if (typingUsers.length === 0) return undefined
    const timer = setInterval(() => {
      setTypingUsers((current) => current.filter((t) => t.expiresAt > Date.now()))
    }, 1000)
    return () => clearInterval(timer)
  }, [typingUsers.length])

  /*
   * Where to be after the list changes.
   *
   * Three cases, and conflating them is what makes a chat window feel broken:
   *
   *   opening a thread     → the newest message, immediately, with no animation
   *   a message arrives    → follow it, but only if they were already at the end
   *   older ones prepended → do not move at all, relative to what they are reading
   *
   * `scrollHeight` rather than a ref on the last element: the element may not
   * have laid out when this runs.
   */
  useLayoutEffect(() => {
    const node = scrollRef.current
    if (!node) return

    const restore = restoreRef.current
    if (restore) {
      restoreRef.current = null
      node.scrollTop = node.scrollHeight - restore.scrollHeight + restore.scrollTop
      return
    }

    if (pinnedRef.current) node.scrollTop = node.scrollHeight
  }, [messages, roomId])

  const announceTyping = useCallback(
    (isTyping) => {
      if (!roomId) return
      if (isTyping === typingSentRef.current) return
      typingSentRef.current = isTyping
      // Through the backend, not straight over the socket: the backend is what
      // knows who is in the room and therefore who should hear it.
      chatApi.typing(roomId, isTyping).catch(() => {})
    },
    [roomId],
  )

  const onDraftChange = (event) => {
    setDraft(event.target.value)
    announceTyping(true)
    clearTimeout(typingTimerRef.current)
    typingTimerRef.current = setTimeout(() => announceTyping(false), TYPING_IDLE_MS)
  }

  useEffect(() => () => clearTimeout(typingTimerRef.current), [])

  const addFiles = useCallback((incoming) => {
    setError(null)
    setPendingFiles((current) => {
      // De-duplicated by name+size: dropping the same file twice is easy to do
      // and there is no reason to send it twice.
      const seen = new Set(current.map((f) => `${f.name}:${f.size}`))
      const next = incoming.filter((f) => !seen.has(`${f.name}:${f.size}`))
      return [...current, ...next].slice(0, 10)
    })
  }, [])

  /*
   * ── The editor is offered, never imposed ───────────────────────────────────
   *
   * An earlier version of this opened the editor automatically on the first
   * image in a drop. It is the wrong default twice over: most images are sent
   * exactly as they were taken, and a modal between picking a file and sending
   * it puts a step in the way of the common case — and a five-image drop is a
   * batch, so stopping to edit each one in turn is worse than editing none.
   *
   * So the file is simply staged, and its chip carries an Edit button. That is
   * still before anything reaches the server, which is the part that matters:
   * a redaction offered after sending is a redaction applied to a copy that has
   * already left.
   */

  /** Swap the original for the edited version, in place, keeping its position. */
  const replaceStaged = useCallback((original, edited) => {
    setPendingFiles((current) => current.map((f) => (f === original ? edited : f)))
    setEditing(null)
  }, [])

  const send = async (event) => {
    event?.preventDefault()
    const body = draft.trim()
    if ((!body && pendingFiles.length === 0) || sending) return

    setSending(true)
    clearTimeout(typingTimerRef.current)
    announceTyping(false)

    try {
      if (pendingFiles.length > 0) {
        /*
         * Files and the text go in one request, and the text becomes the first
         * file's caption. Sending them separately means two messages that can
         * arrive out of order, so "here is the wrong one, ignore it" can land
         * above the file it is about.
         */
        setUploadProgress(0)
        const result = await uploadApi.toChatRoom(roomId, pendingFiles, {
          body: body || undefined,
          onProgress: setUploadProgress,
        })

        setMessages((current) => {
          const known = new Set(current.map((m) => m.id))
          return [...current, ...(result.messages || []).filter((m) => !known.has(m.id))]
        })

        // Per-file rejections come back alongside the successes, so four good
        // files still send and the fifth says why it did not.
        if ((result.rejected || []).length > 0) {
          setError(result.rejected.map((r) => `${r.file_name}: ${r.message}`).join(" · "))
        }
        setPendingFiles([])
        setDraft("")
      } else {
        const result = await chatApi.send(roomId, { body })
        setDraft("")
        setMessages((current) => (current.some((m) => m.id === result.message.id) ? current : [...current, result.message]))
      }
      onRoomChanged?.()
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setSending(false)
      setUploadProgress(null)
    }
  }

  const typingLabel = useMemo(() => {
    if (typingUsers.length === 0) return null
    const names = typingUsers.map((t) => t.sender_name || "Someone")
    if (names.length === 1) return `${names[0]} is typing…`
    if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`
    return `${names[0]} and ${names.length - 1} others are typing…`
  }, [typingUsers])

  if (!roomId) {
    return <EmptyState icon={ForumIcon} title="No conversation open" description="Pick a conversation from the list, or start a new one." />
  }
  if (loading) return <LoadingScreen minHeight={320} />
  if (!room) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || "This conversation is not available."}</Alert>
      </Box>
    )
  }

  const peer = room.kind === "dm" ? participants.find((p) => Number(p.user_id) !== Number(user?.id)) : null
  const title = room.kind === "dm" ? peer?.name || peer?.email || "Direct message" : room.topic || room.title || "Conversation"
  const canAttach = can("chat", "attach_records")

  return (
    <>
      <Box sx={{ px: 2.5, py: 1.75 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h3" noWrap>
              {title}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
              {room.client_id !== "0" && room.client_label ? (
                <Chip size="small" label={room.client_label} sx={{ height: 20, fontSize: 11 }} />
              ) : null}
              <Typography variant="caption" color="text.secondary">
                {participants.length} participant{participants.length === 1 ? "" : "s"}
              </Typography>
              {room.project_name ? (
                <Chip size="small" color="primary" variant="outlined" label={`${room.project_code} · ${room.project_name}`} sx={{ height: 20, fontSize: 11 }} />
              ) : null}
              {room.task_title ? (
                <Chip size="small" variant="outlined" label={`${room.task_code} · ${room.task_title}`} sx={{ height: 20, fontSize: 11 }} />
              ) : null}
            </Stack>
          </Box>

          {canAttach && room.kind !== "dm" ? (
            <Tooltip title="Attach a project or task to this conversation">
              <IconButton size="small" onClick={() => setAttachOpen(true)} aria-label="Attach a project or task">
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}

          <Tooltip title="Who is in this conversation">
            <IconButton size="small" onClick={() => setSettingsOpen(true)} aria-label="Conversation settings">
              <PeopleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      <Divider />

      {/*
        The drop zone is part of the flex column, not a wrapper around it.

        Without this it sized to its own content, so the scroller inside it was
        always exactly as tall as the messages — nothing to scroll within, the
        composer pushed down the page instead of sitting at the bottom of the
        card, and "scroll to the newest" was a no-op on a container with no
        overflow. `minHeight: 0` is the other half: a flex item's default
        `min-height: auto` refuses to shrink below its content, which puts the
        overflow back on the page.
      */}
      <DropZone
        onFiles={addFiles}
        disabled={Boolean(room.closed_at) || sending}
        label="Drop files to send"
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <Box
          ref={scrollRef}
          onScroll={onScroll}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: 2.5,
            py: 2,
            // Overscroll inside the thread must not start scrolling the page
            // behind it once the top or bottom is reached.
            overscrollBehavior: "contain",
          }}
        >
        {/*
          The rest of the history, fetched as they reach for it.

          Deliberately not a "load more" button: reading upwards is a continuous
          gesture, and a button in the middle of it is a full stop. The spinner
          occupies the space the incoming page will fill, so the list does not
          lurch when it lands.
        */}
        {hasMore && messages.length > 0 ? (
          <Stack alignItems="center" sx={{ py: 1.5 }}>
            {loadingMore ? (
              <CircularProgress size={18} />
            ) : (
              <Button size="small" onClick={loadOlder} sx={{ color: "text.secondary" }}>
                Load earlier messages
              </Button>
            )}
          </Stack>
        ) : messages.length > 0 ? (
          <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ py: 1.5 }}>
            This is the beginning of the conversation.
          </Typography>
        ) : null}

        {messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 6 }}>
            No messages yet. Say something.
          </Typography>
        ) : (
          messages.map((message, index) => {
            const previous = messages[index - 1]
            // Consecutive messages from one person are grouped: the avatar and
            // name appear once rather than on every line.
            const grouped =
              previous &&
              previous.created_by === message.created_by &&
              message.type !== "system" &&
              previous.type !== "system" &&
              new Date(message.created_at) - new Date(previous.created_at) < 5 * 60 * 1000

            /* A date separator whenever the day changes, so "9:14" means
               something. Without it a thread read on Monday shows three days of
               times with no way to tell them apart. */
            const dayChanged =
              !previous || new Date(message.created_at).toDateString() !== new Date(previous.created_at).toDateString()

            return (
              <Box key={message.id}>
                {dayChanged ? <DayDivider at={message.created_at} /> : null}
                <Message
                  message={message}
                  grouped={grouped && !dayChanged}
                  isMine={Number(message.created_by) === Number(user?.id)}
                />
              </Box>
            )
          })
        )}
        </Box>
      </DropZone>

      {typingLabel ? (
        <Typography variant="caption" color="text.secondary" sx={{ px: 2.5, pb: 0.5, fontStyle: "italic" }}>
          {typingLabel}
        </Typography>
      ) : null}

      <Divider />
      <Box component="form" onSubmit={send} sx={{ p: 1.5 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {pendingFiles.length > 0 ? (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {pendingFiles.map((file, index) => (
              <PendingFileChip
                key={`${file.name}-${file.size}-${index}`}
                file={file}
                onEdit={setEditing}
                progress={uploadProgress}
                onRemove={() => setPendingFiles((current) => current.filter((_, i) => i !== index))}
              />
            ))}
          </Stack>
        ) : null}

        {uploadProgress !== null ? (
          <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 1, borderRadius: 1 }} />
        ) : null}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(event) => {
            addFiles([...event.target.files])
            // Reset the input, or picking the same file twice in a row fires no
            // change event the second time.
            event.target.value = ""
          }}
        />

        <TextField
          fullWidth
          multiline
          maxRows={5}
          size="small"
          placeholder={
            room.closed_at
              ? "This conversation is closed"
              : pendingFiles.length > 0
                ? "Add a message with these files…"
                : "Write a message…"
          }
          value={draft}
          onChange={onDraftChange}
          disabled={Boolean(room.closed_at) || sending}
          onPaste={(event) => {
            // A pasted screenshot is the most common attachment in a support
            // thread, and Ctrl+V is how people expect to send one.
            const files = [...(event.clipboardData?.files || [])]
            if (files.length > 0) {
              event.preventDefault()
              addFiles(files)
            }
          }}
          onKeyDown={(event) => {
            // Enter sends, Shift+Enter is a newline. The reverse is the single
            // most complained-about choice a chat input can make.
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault()
              send()
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Tooltip title="Attach files">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={Boolean(room.closed_at) || sending}
                        aria-label="Attach files"
                      >
                        <AttachIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    type="submit"
                    size="small"
                    color="primary"
                    disabled={(!draft.trim() && pendingFiles.length === 0) || sending}
                    aria-label="Send message"
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        {/* House behaviour nobody was told about. The first message somebody
            tries to write across two lines goes out half-finished, and they
            learn the rule by breaking it in front of a client. */}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: "5px", fontSize: 11 }}>
          Enter sends · Shift + Enter for a new line
        </Typography>
      </Box>

      <ConversationSettings
        open={settingsOpen}
        room={room}
        participants={participants}
        onClose={() => setSettingsOpen(false)}
        onChanged={() => {
          load()
          onRoomChanged?.()
        }}
      />

      <ImageEditorDialog
        open={Boolean(editing)}
        file={editing}
        onCancel={() => setEditing(null)}
        onSave={(edited) => replaceStaged(editing, edited)}
      />

      <AttachWorkDialog
        open={attachOpen}
        room={room}
        onClose={() => setAttachOpen(false)}
        onAttached={(updated) => {
          setAttachOpen(false)
          setRoom(updated)
          load()
          onRoomChanged?.()
        }}
      />
    </>
  )
}

/** A day's worth of messages gets a heading, so a bare time means something. */
function DayDivider({ at }) {
  const day = new Date(at)
  const today = new Date()
  const yesterday = new Date(today.getTime() - 86400000)
  const label =
    day.toDateString() === today.toDateString()
      ? "Today"
      : day.toDateString() === yesterday.toDateString()
        ? "Yesterday"
        : formatDate(at, "dddd, MMMM D")

  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ my: 2 }}>
      <Divider sx={{ flex: 1 }} />
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
        {label}
      </Typography>
      <Divider sx={{ flex: 1 }} />
    </Stack>
  )
}

/**
 * One message.
 *
 * ── Why yours sit on the right ──────────────────────────────────────────────
 *
 * Every flat-row chat has the same problem: to find your own last message you
 * have to read the names. Side is a pre-attentive cue — you know which messages
 * are yours before you have read a word, which is the whole reason the
 * convention exists.
 *
 * It is side AND surface, not side alone. A bubble on the right in the same
 * colour as one on the left still has to be located by its edge, and in a
 * narrow column the two edges are close together. Yours carries a brand-tinted
 * fill; theirs carries the card's own surface with a border.
 *
 * ── Why the avatar is only on theirs ────────────────────────────────────────
 *
 * You know what you look like. Spending 32 pixels a row telling you is 32 pixels
 * not spent on the message, and on a 330px-wide thread that is a wrapped line.
 */
function Message({ message, grouped, isMine }) {
  if (message.type === "system") {
    return (
      <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ py: 1.25, fontStyle: "italic" }}>
        {message.body}
      </Typography>
    )
  }

  const author = message.author_name || message.author_email || "Unknown"

  return (
    <Stack
      direction="row"
      spacing={1.25}
      sx={{
        mb: grouped ? 0.25 : 1.25,
        mt: grouped ? 0 : 0.75,
        justifyContent: isMine ? "flex-end" : "flex-start",
        // Each message fades up as it lands. `backwards` so it is not painted at
        // full opacity for a frame first.
        ...rise(0, { duration: MOTION.fast }),
      }}
    >
      {!isMine ? (
        <Box sx={{ width: 32, flexShrink: 0 }}>
          {!grouped ? (
            <Avatar sx={{ width: 32, height: 32, bgcolor: avatarColor(author), fontSize: 12 }}>{initialsOf(author)}</Avatar>
          ) : null}
        </Box>
      ) : null}

      <Box sx={{ minWidth: 0, maxWidth: "min(78%, 680px)" }}>
        {!grouped ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="baseline"
            sx={{ mb: 0.25, justifyContent: isMine ? "flex-end" : "flex-start" }}
          >
            {/* The name is dropped on your own: the side already says it, and
                "You · 2 minutes ago" above every one of your messages is a label
                for something nobody was unsure about. */}
            {!isMine ? (
              <Typography variant="body2" fontWeight={650}>
                {author}
              </Typography>
            ) : null}
            <Tooltip title={formatDateTime(message.created_at)}>
              <Typography variant="caption" color="text.secondary" sx={{ cursor: "default" }}>
                {relativeTime(message.created_at)}
              </Typography>
            </Tooltip>
          </Stack>
        ) : null}

        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 2,
            // The corner nearest the author is squared off, which is what makes
            // a run of messages read as one person speaking rather than as a
            // stack of separate cards.
            ...(isMine ? { borderTopRightRadius: grouped ? 8 : 4 } : { borderTopLeftRadius: grouped ? 8 : 4 }),
            border: 1,
            borderColor: isMine ? "transparent" : "divider",
            /*
             * Somebody else's message is grey, not the card colour.
             *
             * The thread sits on a white card, so a white bubble on it was a
             * bubble made entirely of its own 1px border — legible, but only
             * just, and it meant the two sides of the conversation were a
             * coloured shape and an outline rather than two shapes. #f4f4f4 is
             * the least grey that still reads as a surface against white.
             */
            bgcolor: (theme) =>
              isMine
                ? theme.palette.mode === "dark"
                  ? "rgba(126,211,72,0.16)"
                  : "rgba(126,211,72,0.20)"
                : theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.06)"
                  : "rgb(244, 244, 244)",
            transition: transition(["background-color", "border-color"], MOTION.fast),
          }}
        >
          {message.is_deleted ? (
            <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
              This message was deleted
            </Typography>
          ) : message.type === "file" && message.meta?.file_id ? (
            <Attachment meta={message.meta} />
          ) : (
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {message.body}
              {message.is_edited ? (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                  (edited)
                </Typography>
              ) : null}
            </Typography>
          )}
        </Box>
      </Box>
    </Stack>
  )
}

/**
 * One attachment inside a message.
 *
 * `meta.inline` is the SERVER's decision, taken from the same allow-list the
 * download route serves by. The client never inspects the filename to decide
 * whether something is an image — that is how an SVG ends up rendered as a
 * document on the app's own origin.
 */
function Attachment({ meta }) {
  return (
    <Box sx={{ mt: 0.25 }}>
      {meta.caption ? (
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 0.75 }}>
          {meta.caption}
        </Typography>
      ) : null}

      {meta.inline ? (
        <Stack spacing={0.5} alignItems="flex-start">
          <ImageAttachment file={meta} />
          <Typography variant="caption" color="text.secondary">
            {meta.file_name} · {meta.size_label}
          </Typography>
        </Stack>
      ) : (
        <FileChip file={meta} dense />
      )}
    </Box>
  )
}
